import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { MapPin, PackageCheck, ClipboardSignature } from "lucide-react";
import SignaturePad from "./SignaturePad";
import { formatDateFr } from "../lib/dateFormat";

const ETATS = ["Neuf", "Bon état", "État d'usage", "Endommagé"];

/**
 * Section "À réceptionner sur site" — visible si l'utilisateur connecté a
 * été désigné comme `receiver_id` sur une affectation site issue d'une demande
 * approuvée par un manager/admin. Affichée en haut de MesEquipements.
 */
export default function SiteReceptionsToConfirm({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null); // affectation sélectionnée pour signature

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/equipment-site-assignments/mine-to-receive");
      setItems(r.data || []);
    } catch {
      // silencieux : pas critique si l'endpoint répond vide
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onNotif = (ev) => {
      const t = ev?.detail?.type;
      if (t === "equipment_site_receive_pending" || t === "equipment_site_request_decision") load();
    };
    window.addEventListener("plania:notification", onNotif);
    return () => window.removeEventListener("plania:notification", onNotif);
  }, [load]);

  if (loading && items.length === 0) return null;
  if (items.length === 0) return null;

  return (
    <div className="bg-sky-50 border border-sky-200 rounded-sm p-4" data-testid="site-receptions-pending">
      <div className="flex items-center gap-2 mb-3">
        <PackageCheck size={16} className="text-sky-700" />
        <h3 className="text-sm font-display font-semibold text-sky-900">
          À réceptionner sur site ({items.length})
        </h3>
      </div>
      <p className="text-xs text-sky-800 font-mono mb-3">
        Vous avez été désigné pour confirmer la réception du/des matériel(s) ci-dessous sur site. Signez pour valider.
      </p>
      <div className="space-y-2">
        {items.map((a) => (
          <div
            key={a.id}
            className="bg-white border border-sky-200 rounded-sm p-3 flex items-start gap-3 flex-wrap"
            data-testid={`site-receive-row-${a.id}`}
          >
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm font-semibold text-zinc-900">
                {a.designation_snapshot} <span className="text-zinc-500 font-mono text-xs">({a.ref_id_snapshot})</span>
              </p>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">
                <MapPin size={10} className="inline mr-1" />
                Site : <b>{a.site_name_snapshot || (a.site_id ? a.site_id.slice(0, 8) : "—")}</b>
                {" · "}qté <b>{a.quantite_sortie}</b>{" · état "}<b>{a.etat}</b>
                {" · sortie le "}<b>{formatDateFr(a.date_sortie)}</b>
              </p>
              <p className="text-[11px] text-zinc-500 font-mono mt-0.5">
                Signé par {a.controller_name} le {a.created_at ? formatDateFr(a.created_at.slice(0, 10)) : "—"}
              </p>
            </div>
            <Button
              size="sm" onClick={() => setTarget(a)}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid={`site-receive-sign-${a.id}`}
            >
              <ClipboardSignature size={13} className="mr-1.5" /> Confirmer la réception
            </Button>
          </div>
        ))}
      </div>

      {target && (
        <ConfirmReceptionDialog
          assignment={target}
          onClose={() => setTarget(null)}
          onDone={() => {
            setTarget(null);
            load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

function ConfirmReceptionDialog({ assignment, onClose, onDone }) {
  const [etat, setEtat] = useState(assignment.etat || "Bon état");
  const [note, setNote] = useState("");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const nowLabel = new Date().toLocaleString("fr-FR", {
    dateStyle: "long",
    timeStyle: "short",
  });

  const submit = async () => {
    if (!signature) { toast.error("Signature obligatoire"); return; }
    try {
      setSubmitting(true);
      await api.post(`/equipment-site-assignments/${assignment.id}/confirm-reception`, {
        receiver_signature: signature,
        etat_constate: etat,
        note,
      });
      toast.success("Réception confirmée — le demandeur initial est notifié");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la confirmation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="site-receive-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer la réception sur site</DialogTitle>
          <DialogDescription>
            Signez ci-dessous pour valider la réception du matériel sur site.
          </DialogDescription>
        </DialogHeader>

        {/* Récapitulatif de la réception */}
        <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 space-y-1.5" data-testid="site-receive-summary">
          <div className="text-xs font-mono text-zinc-700">
            <span className="label-uppercase text-[10px] text-sky-700">Date / heure de réception</span>
            <p className="text-zinc-900 font-semibold mt-0.5" data-testid="site-receive-datetime">{nowLabel}</p>
          </div>
          <div className="text-xs font-mono text-zinc-700">
            <span className="label-uppercase text-[10px] text-sky-700">Matériel réceptionné</span>
            <p className="text-zinc-900 font-semibold mt-0.5">
              {assignment.designation_snapshot} × {assignment.quantite_sortie}
              <span className="text-zinc-500"> ({assignment.ref_id_snapshot})</span>
            </p>
          </div>
          <div className="text-xs font-mono text-zinc-700">
            <span className="label-uppercase text-[10px] text-sky-700">Site</span>
            <p className="text-zinc-900 font-semibold mt-0.5">
              {assignment.site_name_snapshot || (assignment.site_id ? assignment.site_id.slice(0, 8) : "—")}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="label-uppercase">État constaté à réception</Label>
            <Select value={etat} onValueChange={setEtat}>
              <SelectTrigger className="mt-1" data-testid="site-receive-etat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ETATS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase">Remarque (optionnel)</Label>
            <Textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              rows={2} className="mt-1"
              placeholder="Précisez tout incident ou réserve à la réception"
              data-testid="site-receive-note"
            />
          </div>
          <div>
            <Label className="label-uppercase">Votre signature</Label>
            <SignaturePad onSave={setSignature} value={signature} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button
            onClick={submit} disabled={!signature || submitting}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="site-receive-submit"
          >
            {submitting ? "Envoi…" : "Valider la réception"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
